/**
 * PATH: packages/email-hub/src/feature/reader-header/command/SnoozeDialog.tsx
 * FILE_ID: EMAILHUB-READERHEADER-SNOOZE-DIALOG-001
 * PURPOSE: Present shared snooze presets from the command-bar overflow.
 * ROLE IN ARCHITECTURE: Controlled presentational overlay.
 * MAIN EXPORTS: ReaderSnoozeDialog.
 * NON-RESPONSIBILITIES: Hosts own API calls, optimistic removal, and rollback.
 * NOTES FOR FUTURE AI: Keep preset arithmetic in resurfaceTimes; never duplicate dates here.
 */
import { Clock, X } from 'lucide-react'

import {
  formatResurfaceTime,
  snoozePresets,
} from '../../lib/resurfaceTimes'

export function ReaderSnoozeDialog({
  open,
  onClose,
  onSelect,
}: {
  open: boolean
  onClose: () => void
  onSelect: (iso: string) => void
}) {
  if (!open) return null
  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[80] cursor-default bg-black/30"
        aria-label="Close snooze options"
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Snooze email"
        className="fixed left-1/2 top-1/2 z-[81] w-[min(340px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-popover shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-border px-3 py-2.5">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-4 w-4" />
            Snooze until
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent pointer-coarse:h-11 pointer-coarse:w-11"
            aria-label="Close snooze options"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-1.5">
          {snoozePresets().map((preset) => (
            <button
              key={preset.key}
              type="button"
              className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-accent"
              onClick={() => {
                onClose()
                onSelect(preset.at.toISOString())
              }}
            >
              <span className="text-sm font-medium">{preset.label}</span>
              <span className="text-[11px] text-muted-foreground">
                {formatResurfaceTime(preset.at)}
              </span>
            </button>
          ))}
        </div>
      </section>
    </>
  )
}
